import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { Plus, Search, Pencil, Loader2, Users, Trash2 } from "lucide-react";

function TeacherDialog({ open, onOpenChange, teacher, onSaved, onDelete }) {
  const isEdit = !!teacher;
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFullName(teacher?.full_name || "");
      setUsername(teacher?.username || "");
      setPassword("");
      setActive(teacher?.active ?? true);
    }
  }, [open, teacher]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { full_name: fullName, username, active };
        if (password) payload.password = password;
        await api.put(`/admin/teachers/${teacher.id}`, payload);
        toast.success("Teacher updated");
      } else {
        await api.post("/admin/teachers", {
          full_name: fullName,
          username,
          password,
        });
        toast.success("Teacher created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit teacher" : "Add teacher"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this teacher's account details." : "Create a new teacher account."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="teacher-form">
          <div className="space-y-2">
            <Label htmlFor="t-name">Full name</Label>
            <Input
              id="t-name"
              data-testid="teacher-fullname-input"
              className="h-11"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="t-username">Username</Label>
            <Input
              id="t-username"
              data-testid="teacher-username-input"
              className="h-11"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="t-pass">
              {isEdit ? "Reset password (optional)" : "Temporary password"}
            </Label>
            <Input
              id="t-pass"
              data-testid="teacher-password-input"
              className="h-11"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isEdit ? "Leave blank to keep current" : "Min 6 characters"}
              required={!isEdit}
            />
            <p className="text-xs text-muted-foreground">
              The teacher will be asked to set their own password on first login.
            </p>
          </div>
          {isEdit && (
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium">Account active</p>
                <p className="text-xs text-muted-foreground">
                  Deactivated teachers cannot log in.
                </p>
              </div>
              <Switch
                checked={active}
                onCheckedChange={setActive}
                data-testid="teacher-active-switch"
              />
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {isEdit ? (
              <Button
                type="button"
                variant="ghost"
                className="h-11 text-destructive hover:text-destructive"
                onClick={() => { onOpenChange(false); onDelete(teacher); }}
                data-testid="teacher-dialog-delete-button"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete
              </Button>
            ) : <span />}
            <Button
              type="submit"
              disabled={saving}
              className="h-11"
              data-testid="teacher-save-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create teacher"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const confirmDeleteTeacher = async () => {
    try {
      const { data } = await api.delete(`/admin/teachers/${deleteTarget.id}`);
      toast.success(
        `Deleted ${data.teacher_deleted} — ${data.time_entries_deleted} time entries removed, ${data.records_preserved} records preserved, ${data.students_unassigned} students unassigned`
      );
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teachers");
      setTeachers(data);
    } catch (e) {
      toast.error("Failed to load teachers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      teachers.filter((t) =>
        t.full_name.toLowerCase().includes(search.toLowerCase())
      ),
    [teachers, search]
  );

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setDialogOpen(true);
  };

  return (
    <div className="max-w-5xl" data-testid="teachers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
            Teachers
          </h1>
          <p className="mt-1 text-muted-foreground">Create and manage teacher accounts.</p>
        </div>
        <Button onClick={openCreate} className="h-11" data-testid="add-teacher-button">
          <Plus className="h-4 w-4 mr-2" /> Add teacher
        </Button>
      </div>

      <div className="relative mt-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="teacher-search-input"
          className="h-11 pl-9"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mt-5 bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="teachers-empty">
            <Users className="h-8 w-8 mx-auto mb-3 opacity-40" />
            {teachers.length === 0 ? "No teachers yet. Add your first teacher." : "No matches."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} data-testid={`teacher-row-${t.id}`}>
                    <TableCell className="font-medium">{t.full_name}</TableCell>
                    <TableCell className="text-muted-foreground">{t.username}</TableCell>
                    <TableCell>{t.student_count}</TableCell>
                    <TableCell>
                      {t.active ? (
                        <Badge className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))] text-white">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(t)}
                        data-testid={`edit-teacher-${t.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1.5" /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(t)}
                        data-testid={`delete-teacher-${t.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <TeacherDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        teacher={editing}
        onSaved={load}
        onDelete={setDeleteTarget}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete teacher?"
        confirmName={deleteTarget?.full_name}
        confirmLabel="Delete permanently"
        impact={
          deleteTarget
            ? `Delete ${deleteTarget.full_name}? This will permanently remove their time entries and unassign them from ${deleteTarget.student_count} student(s). Their daily records will be preserved but marked as "Recorded by: [deleted]". This cannot be undone.`
            : ""
        }
        onConfirm={confirmDeleteTeacher}
      />
    </div>
  );
}
